import { useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import {
  ApiClient,
  BerthsOutput,
  GpsOutput,
  SensorOutput,
} from 'seacube-backend/src/api/client';
import { CombinedError } from 'urql';
import { useSnapshot } from 'valtio';

import { reportRequestError } from '@/store';

import {
  resetDisplayedData,
  setFetchLimits,
  setGpsData,
  setInitialFetch,
  setIsLoading,
  setLegs,
  setLegsAugmented,
  setRawPowerOutages,
  setReeferKpi,
  setSensorData,
  setShowFuel,
  store,
} from '../store';
import { setBerths, setGeofences } from '../store/mapLayers';
import { setRawTemperatureBreaches } from '../store/temperatureBreaches';

// Modifié ici

const { VITE_GREENSEE_API_URL, VITE_AUTH0_AUDIENCE } = import.meta.env;

export const useFetchData = () => {
  const { tsFetchRange, reeferId, powerOutages, initialFetch } =
    useSnapshot(store);

  const { getAccessTokenSilently } = useAuth0();

  async function initAssetId() {
    setIsLoading(true);

    try {
      if (!reeferId) return;

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: VITE_AUTH0_AUDIENCE,
          redirect_uri: window.location.origin,
        },
        detailedResponse: true,
      });

      const client = new ApiClient({
        TOKEN: token.access_token,
        BASE: VITE_GREENSEE_API_URL,
      });
      const eventResp = await client.default.getLeg(
        reeferId,
        0,
        7286515527000, //Tuesday, November 25, 2200 4:05:27 PM
        1,
      );
      if (!eventResp.data.length) {
        resetDisplayedData();
        return;
      }
      setFetchLimits([dayjs(eventResp.data[0].start_datetime), dayjs()]);

      await fetchData();
      setInitialFetch(true);
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }
  async function fetchData() {
    const { tsFetchRange } = store;
    if (!reeferId) return;
    if (!tsFetchRange) return;
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: VITE_AUTH0_AUDIENCE,
          redirect_uri: window.location.origin,
        },
        detailedResponse: true,
      });

      // Décodage du token et vérification que x-hasura-user-company est Prometheus
      const decodedToken: any = jwtDecode(token.access_token);
      const hasuraCompany =
        decodedToken['https://hasura.io/jwt/claims']?.['x-hasura-user-company'];
      setShowFuel(hasuraCompany === 'Prometheus');

      const client = new ApiClient({
        TOKEN: token.access_token,
        BASE: VITE_GREENSEE_API_URL,
      });

      let [
        gpsResp,
        sensorResp,
        legResp,
        powerOutageResp,
        temperatureBreachResp,
        reeferKpiResp,
        berthsResp,
        geofencesResp,
      ] = await Promise.all([
        client.default.getGps(reeferId, tsFetchRange[0], tsFetchRange[1]),
        client.default.getSensor(reeferId, tsFetchRange[0], tsFetchRange[1]),
        client.default.getLeg(reeferId, tsFetchRange[0], tsFetchRange[1]),
        client.default.getPowerOutage(
          reeferId,
          tsFetchRange[0],
          tsFetchRange[1],
        ),
        client.default.getTemperatureBreach(
          reeferId,
          tsFetchRange[0],
          tsFetchRange[1],
        ),
        client.default.postReeferKpi({
          reefer_id: reeferId,
          start: dayjs(tsFetchRange[0]).toISOString(),
          end: dayjs(tsFetchRange[1]).toISOString(),
        }),
        client.default.getBerths(),
        client.default.getGeofences(),
      ]);

      const gpsRespParsed: GpsOutput = JSON.parse(gpsResp as unknown as string);
      const sensorRespParsed: SensorOutput = JSON.parse(
        sensorResp as unknown as string,
      );

      setBerths(berthsResp.data ?? []);
      setGeofences(geofencesResp.data ?? []);
      setReeferKpi(reeferKpiResp);

      setRawTemperatureBreaches(temperatureBreachResp.data);
      setRawPowerOutages(powerOutageResp.data);
      setLegs(legResp.data);
      setGpsData(gpsRespParsed.data);
      setSensorData(sensorRespParsed.data);
    } catch (e) {
      reportRequestError(e as CombinedError, () => {});
    }
  }

  useEffect(() => {
    if (!reeferId) return;
    // On récupère la plage de données du reefer, puis on récupère les données une première fois.
    initAssetId();
  }, [reeferId]);

  useEffect(() => {
    if (!initialFetch) return;
    async function fetch() {
      try {
        setIsLoading(true);
        await fetchData();
      } finally {
        setIsLoading(false);
      }
    }
    fetch();
  }, [tsFetchRange]);

  useEffect(() => {
    setLegsAugmented();
  }, [powerOutages]);
};
